import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PhoneIncoming, Search, BellRing, CheckCircle2, UserCheck, ExternalLink } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { ROUTES } from '@/routes/paths';
import {
  useCallerLookup,
  useNotifyContactOwner,
  callerLookupError,
  type CallerLookupResult,
} from '../hooks/useCallerLookup';

interface CallerLookupModalProps {
  open: boolean;
  onClose: () => void;
}

const NOTE_MAX = 300;

/**
 * A customer rings and turns out to be a colleague's client. A rep's own contact
 * search only covers contacts they created, so it comes back empty and a known
 * client gets treated as a new enquiry. This searches the whole directory for one
 * exact number, says whose client it is, and lets the rep notify that BDM in one
 * tap. Only the contact's name, company and owner are shown — nothing else about
 * the record crosses to the person who took the call.
 */
export function CallerLookupModal({ open, onClose }: CallerLookupModalProps) {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<CallerLookupResult | null>(null);
  /** Contacts whose owner has been notified from this dialog. */
  const [notified, setNotified] = useState<Set<string>>(new Set());

  const lookup = useCallerLookup();
  const notify = useNotifyContactOwner();

  const digits = phone.replace(/\D/g, '');
  const validNumber = digits.length >= 10;

  const reset = () => {
    setPhone('');
    setNote('');
    setResult(null);
    setNotified(new Set());
    lookup.reset();
  };
  const handleClose = () => {
    onClose();
    // Deferred so the dialog does not visibly clear itself during the fade-out.
    setTimeout(reset, 200);
  };

  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validNumber) { toast.error('Enter the full 10-digit mobile number.'); return; }
    try {
      setResult(await lookup.mutateAsync(phone.trim()));
      setNotified(new Set());
    } catch (err) {
      setResult(null);
      toast.error(callerLookupError(err, 'Could not search for that number.'));
    }
  };

  const notifyOwner = async (contactId: string, ownerName: string) => {
    try {
      await notify.mutateAsync({ contactId, note: note.trim() });
      setNotified((prev) => new Set(prev).add(contactId));
      toast.success(`${ownerName} has been notified.`);
    } catch (err) {
      toast.error(callerLookupError(err, 'Could not notify them.'));
    }
  };

  const openContact = (id: string) => { handleClose(); navigate(`/contacts/${id}`); };
  // Only the Super Admin is told whose a contact is (and can notify them);
  // for everyone else a match is simply a shared contact to open.
  const someoneElses = result?.matches.filter((m) => !m.isMine && m.owner) ?? [];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="md"
      title={<span className="inline-flex items-center gap-2"><PhoneIncoming className="w-5 h-5" /> Caller lookup</span>}
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          A customer called? Search their number across{' '}
          <span className="font-semibold text-slate-700">all</span> contacts and open the one that matches.
        </p>

        <form onSubmit={search} className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              name="caller-phone"
              label="Caller's mobile number"
              type="tel"
              inputMode="tel"
              autoFocus
              placeholder="98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              leftIcon={<PhoneIncoming className="w-4 h-4" />}
            />
          </div>
          <Button type="submit" loading={lookup.isPending} disabled={!validNumber}>
            <Search className="w-4 h-4 mr-1.5" /> Search
          </Button>
        </form>

        {result && result.matches.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
            <div className="font-semibold text-slate-800">Not in anyone's contacts</div>
            <p className="mt-1 text-xs text-slate-500">
              No contact anywhere in the directory has this number, so this looks like a new customer.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => { handleClose(); navigate(ROUTES.contactNew); }}
            >
              Add as a new contact
            </Button>
          </div>
        )}

        {someoneElses.length > 0 && (
          <div>
            <label htmlFor="caller-note" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Note for the owner <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              id="caller-note"
              value={note}
              maxLength={NOTE_MAX}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. Asking for a quote on 200L drums, wants a call back today"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mt-0.5 text-right text-data-xs text-slate-400">{note.length}/{NOTE_MAX}</div>
          </div>
        )}

        {result?.matches.map((m) => {
          const done = notified.has(m.id);
          const ownerName = m.owner?.name || 'the owner';
          return (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-900">{m.name}</div>
                  {m.company && <div className="truncate text-xs text-slate-500">{m.company}</div>}
                </div>
                {m.isMine ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-data-xs font-bold text-emerald-700">
                    <UserCheck className="w-3.5 h-3.5" /> Your contact
                  </span>
                ) : m.owner ? (
                  <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-data-xs font-bold text-amber-700">
                    Belongs to {ownerName}{m.owner?.username ? ` (${m.owner.username})` : ''}
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-data-xs font-bold text-slate-600">
                    In Contacts
                  </span>
                )}
              </div>

              <div className="mt-3 flex justify-end gap-2">
                {!m.isMine && m.owner && (
                  <Button variant="outline" size="sm" onClick={() => openContact(m.id)}>
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Open contact
                  </Button>
                )}
                {m.isMine || !m.owner ? (
                  <Button variant="outline" size="sm" onClick={() => openContact(m.id)}>
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Open contact
                  </Button>
                ) : done ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> {ownerName} notified
                  </span>
                ) : (
                  <Button
                    size="sm"
                    loading={notify.isPending && notify.variables?.contactId === m.id}
                    onClick={() => notifyOwner(m.id, ownerName)}
                  >
                    <BellRing className="w-4 h-4 mr-1.5" /> Notify {ownerName}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
